import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../common/Modal";
import { Button } from "../ui";
import { getMemberById, reactivateMembership, getReactivationFeeQuote } from "../../api/membershipAPI";
import { getAthleteById } from "../../api/athletesAPI";
import { useHasPermissions } from "../../hooks/useHasPermission";
import { useParticipantLabel } from "../../hooks/useParticipantLabel";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import {
  onEnrollmentCreated,
  EnrollmentCreatedInfo,
  notifyMembershipReactivated,
} from "../../utils/enrollmentCreatedSignal";

/**
 * Offers to reactivate an INACTIVE membership whenever an athlete on it is
 * enrolled — from any screen. Mounted once, in the staff app shell; the
 * enrollment API calls raise the signal (utils/enrollmentCreatedSignal).
 *
 * Why (2026-09-30): a member said "I haven't been getting monthly membership
 * dues". Their athlete kept training while the membership was inactive, and
 * monthly dues bill only ACTIVE memberships with ACTIVE athletes. Dan: "there
 * should be a prompt to reactivate an inactive membership when any enrollment
 * is made ever".
 *
 * Reactivating does what the membership's own reactivate does (sets the
 * membership active and bills the registration fee when the club's fee rule
 * says one is due — the same rule on every reactivation path since
 * 2026-10-11) and brings back the athletes that were just enrolled in the
 * same request, since membership reactivation leaves athletes inactive and
 * dues would still skip them.
 *
 * The prompt waits until no other dialog is open, so it never stacks on top
 * of a form or confirmation that is still on screen.
 */

interface PendingReactivation {
  membershipId: string;
  memberName: string;
  athletes: { id: string; name: string; status: string | null }[];
}

// Only consulted while this prompt is NOT on screen, so any open modal
// dialog belongs to someone else.
const isOtherDialogOpen = () => !!document.querySelector('[role="dialog"][aria-modal="true"]');

const MembershipReactivationPrompt: React.FC = () => {
  const { manage_memberships: canReactivate } = useHasPermissions("manage_memberships");
  const participant = useParticipantLabel();
  const { isDarkMode } = useTheme();

  const [queue, setQueue] = useState<PendingReactivation[]>([]);
  const [showing, setShowing] = useState(false);
  // What the club's fee rule would bill for THIS family:
  // undefined = not loaded (or the read failed).
  const [feeQuote, setFeeQuote] = useState<{ charge: boolean; amount: number; reason: string } | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const current = queue[0] || null;

  const handleSignal = useCallback(async (info: EnrollmentCreatedInfo) => {
    const token = localStorage.getItem("authToken");
    try {
      let membershipId = info.membershipId || null;
      let athlete: any = null;
      if (info.athleteId) {
        const res: any = await getAthleteById(token, info.athleteId);
        athlete = res?.data?.id ? res.data : null;
        membershipId = membershipId || athlete?.membership_id || null;
      }
      if (!membershipId) return;
      const mid: string = membershipId;

      const memberRes: any = await getMemberById(token, membershipId);
      const membership = memberRes?.data;
      // Only a lapsed MEMBER is offered reactivation. Leads convert through
      // their own flow; trial/archived are deliberate states, not lapses.
      if (!membership || membership.status !== "inactive" || membership.account_type === "lead") return;

      const athleteEntry = athlete
        ? [{
            id: athlete.id,
            name: `${athlete.first_name || ""} ${athlete.last_name || ""}`.trim(),
            status: athlete.status ?? null,
          }]
        : [];

      // A second athlete from the same family while the question is still
      // pending joins that question instead of asking twice.
      setQueue((prev) => {
        const existing = prev.find((p) => p.membershipId === mid);
        if (existing) {
          const merged = athleteEntry.filter((a) => !existing.athletes.some((e) => e.id === a.id));
          if (merged.length === 0) return prev;
          return prev.map((p) =>
            p.membershipId === mid ? { ...p, athletes: [...p.athletes, ...merged] } : p,
          );
        }
        return [
          ...prev,
          {
            membershipId: mid,
            memberName: `${membership.first_name || ""} ${membership.last_name || ""}`.trim() || "This member",
            athletes: athleteEntry,
          },
        ];
      });
    } catch {
      // Best effort: a failed lookup must not get in the way of enrolling.
    }
  }, []);

  useEffect(() => {
    if (!canReactivate) return;
    return onEnrollmentCreated((info) => {
      void handleSignal(info);
    });
  }, [canReactivate, handleSignal]);

  // Show the next queued prompt once nothing else is on screen.
  useEffect(() => {
    if (!current || showing) return;
    if (!isOtherDialogOpen()) {
      setShowing(true);
      return;
    }
    const observer = new MutationObserver(() => {
      if (!isOtherDialogOpen()) {
        observer.disconnect();
        setShowing(true);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [current, showing]);

  const currentMembershipId = current?.membershipId || null;
  useEffect(() => {
    if (!showing || !currentMembershipId) return;
    let cancelled = false;
    setFeeQuote(undefined);
    getReactivationFeeQuote(localStorage.getItem("authToken"), currentMembershipId)
      .then((res: any) => {
        if (!cancelled && res?.success) setFeeQuote(res.data);
      })
      .catch(() => {
        // Left undefined: the prompt says the club's rule applies instead of
        // guessing a number.
      });
    return () => {
      cancelled = true;
    };
  }, [showing, currentMembershipId]);

  const dismiss = () => {
    setShowing(false);
    setFeeQuote(undefined);
    setQueue((prev) => prev.slice(1));
  };

  const handleReactivate = async () => {
    if (!current) return;
    const token = localStorage.getItem("authToken");
    setSubmitting(true);
    const wantedIds = current.athletes.filter((a) => a.status !== "active").map((a) => a.id);
    let response: any = null;
    try {
      // The athletes come back in the same request as the membership.
      response = await reactivateMembership(token, current.membershipId, {
        status: "active",
        userRole: localStorage.getItem("userRole"),
        ...(wantedIds.length > 0 ? { athlete_ids: wantedIds } : {}),
      });
    } catch (err: any) {
      toast.error(
        `Couldn't reactivate ${current.memberName}'s membership: ${
          err?.response?.data?.error || err?.message || "unknown error"
        }`,
      );
      setSubmitting(false);
      return;
    }

    const reactivatedAthleteIds: string[] = Array.isArray(response?.reactivated_athlete_ids)
      ? response.reactivated_athlete_ids
      : [];
    const failed: string[] = current.athletes
      .filter((a) => wantedIds.includes(a.id) && !reactivatedAthleteIds.includes(a.id))
      .map((a) => a.name || `the ${participant.singularLower}`);
    // Pages holding the old "inactive" in a form reload before anyone can
    // Save it back (see notifyMembershipReactivated).
    notifyMembershipReactivated({ membershipId: current.membershipId, athleteIds: reactivatedAthleteIds });

    if (failed.length) {
      toast.warning(
        `${current.memberName}'s membership is active, but ${failed.join(", ")} could not be reactivated. Reactivate them from their profile or dues will skip them.`,
      );
    } else {
      const fee = response?.reactivation_fee;
      const feeNote = fee?.charged ? ` The $${Number(fee.amount).toFixed(2)} registration fee was billed.` : "";
      toast.success(`${current.memberName}'s membership is active again. Monthly dues will bill as usual.${feeNote}`);
    }
    setSubmitting(false);
    dismiss();
  };

  if (!canReactivate || !current || !showing) return null;

  const inactiveAthletes = current.athletes.filter((a) => a.status !== "active" && a.name);
  const enrolledNames = current.athletes.map((a) => a.name).filter(Boolean);

  return (
    <Modal isOpen onClose={dismiss} title={`Reactivate ${current.memberName}'s membership?`}>
      <div className={`space-y-3 text-sm ${getSecondaryText(isDarkMode)}`}>
        <p className={getPrimaryText(isDarkMode)}>
          {enrolledNames.length ? `${enrolledNames.join(", ")} was just enrolled, but ` : "An enrollment was just made, but "}
          {current.memberName}'s membership is <strong>inactive</strong>, so it will not be billed monthly dues.
          Do you want to reactivate the membership?
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            {feeQuote === undefined
              ? "The club's registration fee rule decides whether reactivating bills a fee."
              : feeQuote.charge
                ? `Reactivating bills the $${Number(feeQuote.amount).toFixed(2)} registration fee (the club's fee rule).`
                : feeQuote.reason === "within_cycle"
                  ? "No registration fee: this family is still inside a fee cycle it paid for."
                  : "Reactivating adds no registration fee."}
          </li>
          {inactiveAthletes.length > 0 && (
            <li>
              {inactiveAthletes.map((a) => a.name).join(", ")} will also be set back to active.
            </li>
          )}
        </ul>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={dismiss} disabled={submitting}>
            Not now
          </Button>
          <Button variant="primary" onClick={handleReactivate} loading={submitting}>
            Reactivate membership
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MembershipReactivationPrompt;
